/**
 * Custom error classes for report generation
 */

export class ImageCaptureError extends Error {
  constructor(message: string, public readonly elementType: string) {
    super(message);
    this.name = 'ImageCaptureError';
  }
}

export class CompressionError extends Error {
  constructor(message: string, public readonly originalSize?: number) {
    super(message);
    this.name = 'CompressionError';
  }
}

export class TimeoutError extends Error {
  constructor(message: string, public readonly timeoutMs: number) {
    super(message);
    this.name = 'TimeoutError';
  }
}

export class ValidationError extends Error {
  constructor(message: string, public readonly field?: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class PDFGenerationError extends Error {
  constructor(message: string, public readonly stage?: string) {
    super(message);
    this.name = 'PDFGenerationError';
  }
}

/**
 * Error logger utility
 */
export class ErrorLogger {
  private static isDevelopment = process.env.NODE_ENV === 'development';

  /**
   * Log error to console (development only)
   */
  static logError(error: Error, context?: string): void {
    if (this.isDevelopment) {
      console.error(`[ReportComponent${context ? ` - ${context}` : ''}]:`, error);
    }
  }

  /**
   * Log warning to console (development only)
   */
  static logWarning(message: string, context?: string): void {
    if (this.isDevelopment) {
      console.warn(`[ReportComponent${context ? ` - ${context}` : ''}]:`, message);
    }
  }

  /**
   * Log info to console (development only)
   */
  static logInfo(message: string, context?: string): void {
    if (this.isDevelopment) {
      console.info(`[ReportComponent${context ? ` - ${context}` : ''}]:`, message);
    }
  }

  /**
   * Get user-friendly error message
   */
  static getUserMessage(error: Error): string {
    if (error instanceof ImageCaptureError) {
      return `Falha ao capturar ${error.elementType}. Por favor, tente novamente.`;
    }

    if (error instanceof CompressionError) {
      return 'Falha ao comprimir as imagens. Por favor, tente novamente.';
    }

    if (error instanceof TimeoutError) {
      return 'A operação demorou muito tempo. Por favor, tente novamente.';
    }

    if (error instanceof ValidationError) {
      return error.message;
    }

    if (error instanceof PDFGenerationError) {
      return `Falha ao gerar o PDF${error.stage ? ` durante ${error.stage}` : ''}. Por favor, tente novamente.`;
    }

    // Network errors
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Erro de conexão com o servidor. Tentando gerar PDF localmente...';
    }

    // Default
    return 'Ocorreu um erro inesperado. Por favor, tente novamente.';
  }
}

/**
 * Wraps a promise with timeout
 */
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string
): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new TimeoutError(errorMessage, timeoutMs)), timeoutMs)
    ),
  ]);
}

/**
 * Retry utility for flaky operations
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: Error;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      ErrorLogger.logWarning(`Attempt ${attempt}/${maxRetries} failed: ${lastError.message}`);

      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
      }
    }
  }

  throw lastError!;
}
